import { z } from "zod";
import type { MemberRef } from "./demos.ts";
import type { SessionUse } from "./discord.ts";

/**
 * Voice played along a map's replay: an audio file (a Discord or OBS recording,
 * a phone memo) added to the map, lined up with the demo.
 *
 * A map relaunched after a crash is one replay made of parts, with a pause
 * between them that is not the real one. So a track keeps where it is at the
 * start of each part: `offsets[i]` is the second of the audio at the first tick
 * of part i. A part without its own (null, or past the end of the list) goes on
 * from the part before it, as if the replay's pause were the real one.
 */

/** Formats browsers play: MP3, Ogg (Opus, Vorbis), WebM, M4A/AAC, WAV and FLAC. */
export const VOICE_TYPES = ["audio/mpeg", "audio/ogg", "audio/webm", "audio/mp4", "audio/wav", "audio/flac"] as const;
export type VoiceType = (typeof VOICE_TYPES)[number];

/** As videos (the ingress takes 160 MB): over 5 hours of 64 kbit/s Opus, 2.5 hours of 128 kbit/s MP3. */
export const MAX_VOICE_BYTES = 150 * 1024 * 1024;
export const MAX_VOICE_TRACKS_PER_DEMO = 10;
/** An offset is within a day either way. */
export const MAX_VOICE_OFFSET = 24 * 3600;

/** Where a map's voice comes from: the bot's recording or files people uploaded, never both. */
export type VoiceSource = "bot" | "files";

export interface VoiceTrack {
  id: string;
  /** The bot recording it came from; null for an uploaded file. */
  sessionId: string | null;
  /** The team's uploaded file it plays (0.25.0); null for a bot track. */
  fileId: string | null;
  /** Whose voice, or what it is ("Comms", "Hadim"). */
  label: string;
  contentType: VoiceType;
  bytes: number;
  offsets: (number | null)[];
  uploadedBy: MemberRef | null;
  /** Who added it, the owner and captains can line it up, rename and delete it. */
  canEdit: boolean;
  createdAt: string;
}

const Offset = z.number().finite().min(-MAX_VOICE_OFFSET).max(MAX_VOICE_OFFSET);

export const VoiceLabel = z.string().trim().min(1).max(60);

export const VoiceTrackUpdate = z
  .object({
    label: VoiceLabel.optional(),
    offsets: z.array(Offset.nullable()).max(16).optional(),
  })
  .refine((v) => v.label !== undefined || v.offsets !== undefined, "Nothing to change.");
export type VoiceTrackUpdate = z.input<typeof VoiceTrackUpdate>;

/**
 * A file a player uploaded, kept by the team until deleted (0.25.0): listed in
 * Matches › Recordings beside the bot's recordings, on at most one map.
 */
export interface VoiceFileInfo {
  id: string;
  label: string;
  contentType: VoiceType;
  bytes: number;
  /** Null until a browser has read the file and said how long it is. */
  durationSeconds: number | null;
  uploadedBy: MemberRef | null;
  createdAt: string;
  /** The map(s) playing it; empty when taken off. */
  usedOn: SessionUse[];
  /** Who added it, the owner and captains rename and delete it. */
  canEdit: boolean;
}

export const VoiceFileUpdate = z
  .object({
    label: VoiceLabel.optional(),
    /** Told once by the first browser that reads the file. */
    durationSeconds: z
      .number()
      .finite()
      .min(0)
      .max(24 * 3600)
      .optional(),
  })
  .refine((v) => v.label !== undefined || v.durationSeconds !== undefined, "Nothing to change.");
export type VoiceFileUpdate = z.input<typeof VoiceFileUpdate>;

export const AddFileInput = z.object({
  fileId: z.string().uuid(),
  /** Take the map's bot recording off first. */
  replace: z.boolean().default(false),
});

/** The offsets of every part, the missing ones carried on from the part before. */
export function voiceOffsets(offsets: readonly (number | null)[], partStarts: readonly number[], tickRate: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < Math.max(1, partStarts.length); i++) {
    const own = offsets[i];
    if (own !== null && own !== undefined && Number.isFinite(own)) out.push(own);
    else if (i === 0) out.push(0);
    else out.push(out[i - 1] + (partStarts[i] - partStarts[i - 1]) / tickRate);
  }
  return out;
}

/** The part a tick is in. */
function partOf(partStarts: readonly number[], tick: number) {
  let part = 0;
  while (part + 1 < partStarts.length && partStarts[part + 1] <= tick) part++;
  return part;
}

/** The second of the audio heard at a tick of the replay (negative: before the recording starts). */
export function voiceTime(offsets: readonly (number | null)[], partStarts: readonly number[], tickRate: number, tick: number): number {
  const all = voiceOffsets(offsets, partStarts, tickRate);
  const part = partOf(partStarts, tick);
  return all[part] + (tick - (partStarts[part] ?? 0)) / tickRate;
}

/**
 * Lines a track up so the audio's second `audioAt` is heard at `tick`: changes
 * the offset of that tick's part only, so the other parts stay where they were.
 */
export function syncVoiceAt(offsets: readonly (number | null)[], partStarts: readonly number[], tickRate: number, tick: number, audioAt: number): number[] {
  const all = voiceOffsets(offsets, partStarts, tickRate);
  const part = partOf(partStarts, tick);
  all[part] = round3(audioAt - (tick - (partStarts[part] ?? 0)) / tickRate);
  return all;
}

/** Moves a track by `seconds` in the part a tick is in (positive: the voice comes later). */
export function nudgeVoice(offsets: readonly (number | null)[], partStarts: readonly number[], tickRate: number, tick: number, seconds: number): number[] {
  const all = voiceOffsets(offsets, partStarts, tickRate);
  const part = partOf(partStarts, tick);
  all[part] = round3(all[part] - seconds);
  return all;
}

const round3 = (n: number) => Math.round(n * 1000) / 1000;

/**
 * Recognises an audio file by its first bytes. WebM and Ogg hold video too, and
 * MP4 is also M4A: those are taken as audio, the browser plays their sound.
 */
export function sniffAudio(b: Uint8Array): VoiceType | null {
  const ascii = (from: number, to: number) => String.fromCharCode(...b.subarray(from, to));
  if (b.length >= 3 && ascii(0, 3) === "ID3") return "audio/mpeg";
  // An MPEG audio frame starts with 11 set bits.
  if (b.length >= 2 && b[0] === 0xff && (b[1] & 0xe0) === 0xe0 && (b[1] & 0x06) !== 0) return "audio/mpeg";
  if (b.length >= 4 && ascii(0, 4) === "OggS") return "audio/ogg";
  if (b.length >= 4 && ascii(0, 4) === "fLaC") return "audio/flac";
  if (b.length >= 12 && ascii(0, 4) === "RIFF" && ascii(8, 12) === "WAVE") return "audio/wav";
  if (b.length >= 4 && b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) return "audio/webm";
  if (b.length >= 8 && ascii(4, 8) === "ftyp") return "audio/mp4";
  // ADTS AAC (no container): browsers play it as MP4 audio only in a container, so it is refused.
  return null;
}

export const VOICE_EXT: Record<VoiceType, string> = {
  "audio/mpeg": "mp3",
  "audio/ogg": "ogg",
  "audio/webm": "webm",
  "audio/mp4": "m4a",
  "audio/wav": "wav",
  "audio/flac": "flac",
};
